/** Money and number formatting shared by every page. */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "US$",
  SGD: "S$",
  MYR: "RM",
  AUD: "A$",
  IDR: "Rp",
  THB: "฿",
  TWD: "NT$",
  GBP: "£",
  EUR: "€",
  JPY: "¥",
  KRW: "₩",
  CNY: "CN¥",
  PHP: "₱",
};
const ZERO_DECIMAL = new Set(["IDR", "JPY", "KRW", "TWD"]);

export function formatMoney(amount: number | null | undefined, currency: string, compact = false): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return "–";
  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `;
  const digits = ZERO_DECIMAL.has(currency) ? 0 : 2;
  const body = compact
    ? new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(amount)
    : new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
  return amount < 0 ? `-${symbol}${body.replace("-", "")}` : `${symbol}${body}`;
}

export const formatCount = (n: number) => new Intl.NumberFormat("en-US").format(n);
export const formatPercent = (n: number | null) => (n === null || !Number.isFinite(n) ? "–" : `${(n * 100).toFixed(1)}%`);

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact elapsed time in the largest whole unit: "<1m", "12m", "5h", "3d". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < MINUTE) return "<1m";
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)}m`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h`;
  return `${Math.floor(ms / DAY)}d`;
}

/** "just now", "12m ago", "5h ago", "3d ago". A time slightly in the future
 * (clock skew between server and browser) reads as "just now". */
export function formatAge(from: string | Date, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(from).getTime();
  return ms < MINUTE ? "just now" : `${formatDuration(ms)} ago`;
}
